import { elements, optionalElement } from '../core/dom';
import { UPGRADE_BASE_COSTS, UPGRADE_KINDS, WEAPONS } from '../game/config';
import type { ComboSnapshot } from '../game/ComboSystem';
import type { CameraSettings, Difficulty, GameState, MusicTrackId, UpgradeKind, WeaponKind } from '../game/types';

export interface UIDrop {
  id?: number | string;
  type?: string;
  name?: string;
  label?: string;
  icon?: string;
  amount?: number;
  collected?: boolean;
  status?: string;
}

export interface UILevelProgress {
  current?: number;
  total?: number;
  percent?: number;
  completed?: number;
}

export interface UIStateExtensions {
  playerHp?: number;
  playerMaxHp?: number;
  enemyHp?: number;
  enemyMaxHp?: number;
  targetHp?: number;
  targetMaxHp?: number;
  enemyName?: string;
  currentLevel?: number;
  location?: string | { name?: string; key?: string };
  drops?: Array<UIDrop | string>;
  dropsCollected?: number;
  levelProgress?: number | UILevelProgress;
}

export type UIState = GameState & Partial<UIStateExtensions>;

export interface UIUpdateContext {
  enemyCount?: number;
  waveSize?: number;
  difficulty?: string;
  enemyHp?: number;
  enemyMaxHp?: number;
  enemyName?: string;
  target?: { hp?: number; maxHp?: number; name?: string };
  drops?: Array<UIDrop | string>;
  dropsCollected?: number;
  levelProgress?: number | UILevelProgress;
  location?: string | { name?: string; key?: string };
  combo?: ComboSnapshot;
}

export interface UICallbacks {
  onStart: () => void;
  onRestart: () => void;
  onPause: () => void;
  onSound: () => void;
  onMusicChange?: (track: MusicTrackId) => void;
  onVolumeChange?: (kind: 'music' | 'sound', value: number) => void;
  onDifficultyChange?: (difficulty: Difficulty) => void;
  onCameraChange: (key: keyof CameraSettings, value: number) => void;
  onWeapon: (kind: WeaponKind) => void;
  onUpgrade: (kind: UpgradeKind) => void;
  onLevelSelect?: (level: number) => void;
}

interface ResolvedLevelProgress {
  fraction: number;
  detail: string;
}

const CAMERA_KEYS: Array<keyof CameraSettings> = ['tilt', 'distance', 'fov', 'lookAhead'];

export class UIController {
  private alertTimer: number | undefined;
  private centerTimer: number | undefined;
  private damageTimer: number | undefined;

  constructor(private readonly callbacks: UICallbacks) {
    this.bindEvents();
    this.setDifficulty('normal');
  }

  /**
   * Supports both the original positional call and the object context used by
   * an expanded GameState integration.
   */
  update(state: UIState, context?: UIUpdateContext): void;
  update(state: UIState, enemyCount?: number, waveSize?: number, difficulty?: string): void;
  update(state: UIState, contextOrEnemyCount: UIUpdateContext | number = {}, waveSize = 0, difficulty = 'NORMAL'): void {
    const viewState = state as UIState;
    const context: UIUpdateContext = typeof contextOrEnemyCount === 'number'
      ? { enemyCount: contextOrEnemyCount, waveSize, difficulty }
      : contextOrEnemyCount;
    const resolvedDifficulty = context.difficulty ?? 'NORMAL';
    const resolvedWaveSize = Math.max(1, context.waveSize ?? viewState.waveTotal ?? 1);
    const currentLevel = this.resolveCurrentLevel(viewState);
    const location = this.resolveLocation(context.location ?? viewState.location);
    const wave = `${String(viewState.wave).padStart(2, '0')} / ${String(viewState.waveTotal).padStart(2, '0')}`;
    const score = String(viewState.score).padStart(3, '0');
    const reward = 110 + currentLevel * 40;

    this.text('#levelValue', String(currentLevel).padStart(2, '0'));
    this.text('#locationValue', location);
    this.text('#pauseLocationValue', location);
    this.text('#startLocationValue', location);
    this.text('#startLevelValue', String(currentLevel).padStart(2, '0'));
    this.text('#waveValue', wave);
    this.text('#pauseWaveValue', wave);
    this.text('#scoreValue', score);
    this.text('#pauseScoreValue', score);
    this.text('#creditsValue', String(viewState.credits));
    this.text('#nextReward', `${reward} ₡`);
    this.text('#difficultyValue', resolvedDifficulty);
    this.text('#pauseDifficultyValue', resolvedDifficulty);
    this.text('#enemyCount', String(context.enemyCount ?? 0).padStart(2, '0'));
    this.text('#enemyCountReadout', String(context.enemyCount ?? 0).padStart(2, '0'));
    this.text('#upgradeTotal', String(Object.values(viewState.upgrades).reduce((sum, value) => sum + value, 0)).padStart(2, '0'));

    const playerStatus = viewState.playerShieldTime > 0 ? `SHIELD ${Math.ceil(viewState.playerShieldTime)}s` : 'ARMOR STABLE';
    this.updateHealthBar('#playerHealthValue', '#playerMaxHealthValue', '#playerHealthMeter', '#playerHealthStatus', viewState.playerHp, viewState.playerMaxHp, playerStatus);
    const baseStatus = viewState.baseShieldTime > 0 ? `ЩИТ БАЗЫ ${Math.ceil(viewState.baseShieldTime)}S` : 'CORE VULNERABLE';
    this.updateHealthBar('#baseHealthValue', '#baseMaxHealthValue', '#baseMeter', '#baseStatus', viewState.baseHp, viewState.baseMaxHp, baseStatus);

    const target = context.target;
    const targetHp = target?.hp ?? context.enemyHp ?? viewState.targetHp ?? viewState.enemyHp;
    const targetMaxHp = target?.maxHp ?? context.enemyMaxHp ?? viewState.targetMaxHp ?? viewState.enemyMaxHp;
    const targetName = target?.name ?? context.enemyName ?? viewState.enemyName ?? 'ЦЕЛЬ';
    this.updateTargetHealth(targetHp, targetMaxHp, targetName);

    this.setMeter('#waveMeter', viewState.waveKills / resolvedWaveSize);
    const levelProgress = this.resolveLevelProgress(viewState, context, resolvedWaveSize);
    this.setMeter('#levelProgressMeter', levelProgress.fraction);
    this.text('#levelProgressPercent', `${Math.round(levelProgress.fraction * 100)}%`);
    this.text('#levelProgressDetail', levelProgress.detail);
    const mapLevel = viewState.levelTransition > 0 ? currentLevel + 1 : currentLevel;
    this.updateLevelMap(mapLevel);
    this.updateDrops(context.drops ?? viewState.drops, context.dropsCollected ?? viewState.dropsCollected);
    this.updateCombo(context.combo);

    if (viewState.difficulty) this.setDifficulty(viewState.difficulty);
    if (viewState.musicTrack) this.setMusicTrack(viewState.musicTrack);
    if (typeof viewState.musicVolume === 'number') this.setVolume('music', viewState.musicVolume);
    if (typeof viewState.soundVolume === 'number') this.setVolume('sound', viewState.soundVolume);
    UPGRADE_KINDS.forEach((kind) => this.updateUpgrade(kind, viewState));
    this.updateWeapon(viewState);
  }

  updateCamera(settings: CameraSettings): void {
    this.range('#cameraTilt', settings.tilt);
    this.range('#cameraDistance', settings.distance);
    this.range('#cameraFov', settings.fov);
    this.range('#cameraLookAhead', settings.lookAhead);
    this.text('#cameraTiltValue', `${settings.tilt}°`);
    this.text('#cameraDistanceValue', String(settings.distance));
    this.text('#cameraFovValue', `${settings.fov}°`);
    this.text('#cameraLookAheadValue', settings.lookAhead < 0 ? `−${Math.abs(settings.lookAhead)}` : `+${settings.lookAhead}`);
  }

  setMusicTrack(track: MusicTrackId): void {
    elements<HTMLButtonElement>('[data-music-track]').forEach((button) => {
      const active = button.dataset.musicTrack === track;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', String(active));
    });
  }

  setVolume(kind: 'music' | 'sound', value: number): void {
    const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
    const input = optionalElement<HTMLInputElement>(`[data-volume="${kind}"]`);
    if (input) input.value = String(percent);
    this.text(`#${kind}VolumeValue`, `${percent}%`);
  }

  setDifficulty(difficulty: Difficulty): void {
    elements<HTMLButtonElement>('[data-difficulty]').forEach((button) => {
      const active = button.dataset.difficulty === difficulty;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', String(active));
    });
  }

  setSettingsOpen(open: boolean): void {
    const panel = optionalElement<HTMLElement>('#cameraPanel');
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    this.optionalAttribute('#cameraBtn', 'aria-expanded', String(open));
    this.optionalClass('#cameraBtn', 'is-active', open);
  }

  setGameStarted(): void {
    const overlay = optionalElement<HTMLElement>('#startOverlay');
    overlay?.classList.add('is-hidden');
  }

  setPaused(paused: boolean): void {
    if (!paused) this.setSettingsOpen(false);
    const overlay = optionalElement<HTMLElement>('#pauseOverlay');
    overlay?.classList.toggle('is-visible', paused);
    overlay?.setAttribute('aria-hidden', String(!paused));
    this.optionalAttribute('#pauseBtn', 'aria-pressed', String(paused));
    document.body.classList.toggle('is-paused', paused);
  }

  showGameOver(reason: 'base' | 'player' = 'base'): void {
    optionalElement<HTMLElement>('#startOverlay')?.classList.add('is-hidden');
    const overlay = optionalElement<HTMLElement>('#gameoverOverlay');
    overlay?.classList.add('is-visible');
    overlay?.setAttribute('aria-hidden', 'false');
    const baseStatus = optionalElement<HTMLElement>('#baseStatus');
    if (baseStatus) {
      baseStatus.textContent = reason === 'player' ? 'PILOT SIGNAL LOST' : 'CORE SIGNAL LOST';
      baseStatus.style.color = 'var(--red)';
    }
    this.text('#gameoverTitle', reason === 'player' ? 'ТАНК УНИЧТОЖЕН' : 'БАЗА УНИЧТОЖЕНА');
    this.text('#gameoverCopy', reason === 'player' ? 'Машина потеряна, но протокол можно перезапустить.' : 'Сектор пал, но протокол можно перезапустить.');
  }

  showVictory(): void {
    optionalElement<HTMLElement>('#startOverlay')?.classList.add('is-hidden');
    optionalElement<HTMLElement>('#gameoverOverlay')?.classList.remove('is-visible');
    const overlay = optionalElement<HTMLElement>('#victoryOverlay');
    overlay?.classList.add('is-visible');
    overlay?.setAttribute('aria-hidden', 'false');
  }

  setSound(enabled: boolean): void {
    this.text('#soundBtn', enabled ? '♫ ЗВУК' : '× MUTE');
    this.text('#musicStatus', enabled ? 'AUDIO CHANNEL LIVE' : 'AUDIO CHANNEL MUTED');
  }

  setMusicLive(): void { this.text('#musicStatus', 'AUDIO CHANNEL LIVE'); }

  setWeapon(kind: WeaponKind): void {
    elements<HTMLElement>('.weapon-card').forEach((card) => {
      const active = card.dataset.weapon === kind;
      card.classList.toggle('active', active);
      card.setAttribute('aria-pressed', String(active));
    });
  }

  unlockWeapon(kind: WeaponKind): void {
    const card = optionalElement<HTMLElement>(`.weapon-card[data-weapon="${kind}"]`);
    card?.classList.remove('locked');
    card?.classList.add('unlocked');
  }

  showAlert(message: string, important = false): void {
    const alert = optionalElement<HTMLElement>('#alertText');
    if (!alert) return;
    alert.textContent = message;
    alert.classList.toggle('is-important', important);
    if (this.alertTimer !== undefined) window.clearTimeout(this.alertTimer);
    if (important) {
      this.alertTimer = window.setTimeout(() => alert.classList.remove('is-important'), 1800);
    }
  }

  showCenter(kicker: string, message: string): void {
    const center = optionalElement<HTMLElement>('#centerAlert');
    const kickerElement = optionalElement<HTMLElement>('#centerAlert .alert-kicker');
    if (!center || !kickerElement) return;
    kickerElement.textContent = kicker;
    this.text('#centerAlertText', message);
    center.classList.add('visible');
    if (this.centerTimer !== undefined) window.clearTimeout(this.centerTimer);
    this.centerTimer = window.setTimeout(() => center.classList.remove('visible'), 1650);
  }

  showHitMarker(kill = false, position?: { x: number; y: number }): void {
    const marker = optionalElement<HTMLElement>('#hitMarker');
    if (!marker) return;
    if (position) {
      marker.style.left = `${Math.min(96, Math.max(4, position.x))}%`;
      marker.style.top = `${Math.min(94, Math.max(6, position.y))}%`;
    }
    marker.classList.remove('is-hit', 'is-kill');
    void marker.offsetWidth;
    marker.classList.add(kill ? 'is-kill' : 'is-hit');
  }

  flashDamage(target: 'player' | 'base'): void {
    const vignette = optionalElement<HTMLElement>('#damageVignette');
    if (!vignette) return;
    if (this.damageTimer !== undefined) window.clearTimeout(this.damageTimer);
    vignette.classList.remove('is-player', 'is-base');
    void vignette.offsetWidth;
    vignette.classList.add(target === 'player' ? 'is-player' : 'is-base');
    this.damageTimer = window.setTimeout(() => vignette.classList.remove('is-player', 'is-base'), 420);
  }

  updateClock(elapsedSeconds: number, aimX: number, fieldWidth: number): void {
    const elapsed = Math.floor(elapsedSeconds);
    this.text('#clockValue', `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`);
    this.text('#aimValue', `${aimX >= 0 ? '+' : ''}${aimX.toFixed(1)}`);
    const crosshair = optionalElement<HTMLElement>('#crosshairLine');
    if (crosshair) crosshair.style.left = `${50 + aimX / Math.max(1, fieldWidth) * 100}%`;
  }

  private bindEvents(): void {
    this.onClick('#startBtn', this.callbacks.onStart);
    this.onClick('#restartBtn', this.callbacks.onRestart);
    this.onClick('#victoryRestartBtn', this.callbacks.onRestart);
    this.onClick('#pauseBtn', this.callbacks.onPause);
    this.onClick('#pauseResumeBtn', this.callbacks.onPause);
    this.onClick('#soundBtn', this.callbacks.onSound);
    elements<HTMLButtonElement>('[data-music-track]').forEach((button) => button.addEventListener('click', () => {
      const track = button.dataset.musicTrack;
      if (track !== 'electronic' && track !== 'city' && track !== 'synth') return;
      this.setMusicTrack(track);
      this.callbacks.onMusicChange?.(track);
    }));
    elements<HTMLInputElement>('[data-volume]').forEach((input) => input.addEventListener('input', () => {
      const kind = input.dataset.volume;
      if (kind !== 'music' && kind !== 'sound') return;
      const value = Math.min(1, Math.max(0, Number(input.value) / 100));
      this.setVolume(kind, value);
      this.callbacks.onVolumeChange?.(kind, value);
    }));
    elements<HTMLButtonElement>('[data-difficulty]').forEach((button) => button.addEventListener('click', () => {
      const difficulty = button.dataset.difficulty;
      if (difficulty !== 'easy' && difficulty !== 'normal' && difficulty !== 'hard' && difficulty !== 'extreme') return;
      this.setDifficulty(difficulty);
      this.callbacks.onDifficultyChange?.(difficulty);
    }));
    this.onClick('#cameraBtn', () => {
      const panel = optionalElement<HTMLElement>('#cameraPanel');
      this.setSettingsOpen(!panel?.classList.contains('is-open'));
    });
    this.onClick('#pauseSettingsBtn', () => this.setSettingsOpen(true));
    this.onClick('#cameraClose', () => this.setSettingsOpen(false));

    elements<HTMLInputElement>('[data-camera]').forEach((input) => input.addEventListener('input', () => {
      const key = input.dataset.camera as keyof CameraSettings;
      if (CAMERA_KEYS.includes(key)) this.callbacks.onCameraChange(key, Number(input.value));
    }));
    elements<HTMLButtonElement>('.weapon-card').forEach((card) => card.addEventListener('click', () => {
      const kind = card.dataset.weapon;
      if (kind === 'electro' || kind === 'flame' || kind === 'rocket') this.callbacks.onWeapon(kind);
    }));
    elements<HTMLButtonElement>('.buy-button').forEach((button) => button.addEventListener('click', (event) => {
      event.stopPropagation();
      const kind = button.dataset.upgrade;
      if (kind === 'power' || kind === 'cooling' || kind === 'shield') this.callbacks.onUpgrade(kind);
    }));
    elements<HTMLButtonElement>('.level-node').forEach((button) => button.addEventListener('click', () => {
      if (button.classList.contains('is-locked')) return;
      const level = Number(button.dataset.level);
      if (Number.isFinite(level)) this.callbacks.onLevelSelect?.(level);
    }));
    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const panel = optionalElement<HTMLElement>('#cameraPanel');
      if (panel?.classList.contains('is-open')) {
        this.setSettingsOpen(false);
        event.preventDefault();
        event.stopImmediatePropagation();
      } else if (optionalElement<HTMLElement>('#pauseOverlay')?.classList.contains('is-visible')) {
        this.callbacks.onPause();
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    });
  }

  private updateHealthBar(valueSelector: string, maxSelector: string, meterSelector: string, statusSelector: string, hp: number | undefined, maxHp: number | undefined, stableLabel: string): void {
    const resolvedHp = Math.max(0, hp ?? 100);
    const resolvedMaxHp = Math.max(1, maxHp ?? 100);
    const fraction = Math.min(1, resolvedHp / resolvedMaxHp);
    this.text(valueSelector, String(Math.ceil(resolvedHp)));
    this.text(maxSelector, String(Math.ceil(resolvedMaxHp)));
    this.setMeter(meterSelector, fraction);
    this.text(statusSelector, resolvedHp <= 0 ? 'SIGNAL LOST' : resolvedHp / resolvedMaxHp < 0.35 ? 'CRITICAL DAMAGE' : stableLabel);
    this.setHealthTone(valueSelector, meterSelector, fraction);
  }

  private updateTargetHealth(hp: number | undefined, maxHp: number | undefined, name: string): void {
    const hasTarget = typeof hp === 'number';
    const resolvedHp = Math.max(0, hp ?? 0);
    const resolvedMaxHp = Math.max(1, maxHp ?? hp ?? 1);
    const fraction = hasTarget ? Math.min(1, resolvedHp / resolvedMaxHp) : 0;
    this.text('#enemyHealthName', name);
    this.text('#enemyHealthValue', hasTarget ? String(Math.ceil(resolvedHp)) : '—');
    this.text('#enemyMaxHealthValue', hasTarget ? String(Math.ceil(resolvedMaxHp)) : '—');
    this.setMeter('#enemyHealthMeter', fraction);
    this.text('#enemyHealthStatus', !hasTarget ? 'NO TARGET LOCK' : resolvedHp <= 0 ? 'TARGET DESTROYED' : fraction < 0.35 ? 'CRITICAL DAMAGE' : 'TARGET TRACKED');
    this.optionalClass('#enemyHealthMeter', 'is-empty', !hasTarget);
    this.setHealthTone('#enemyHealthValue', '#enemyHealthMeter', fraction);
  }

  private resolveCurrentLevel(state: UIState): number {
    return Math.max(1, Math.floor(state.currentLevel ?? state.level));
  }

  private resolveLocation(location: UIState['location']): string {
    if (typeof location === 'string' && location.length > 0) return location;
    if (location && typeof location === 'object' && location.name) return location.name;
    if (location && typeof location === 'object' && location.key) return location.key.toUpperCase();
    return 'ПУСТЫННЫЙ РУБЕЖ';
  }

  private resolveLevelProgress(state: UIState, context: UIUpdateContext, waveSize: number): ResolvedLevelProgress {
    const supplied = context.levelProgress ?? state.levelProgress;
    if (typeof supplied === 'number') {
      return { fraction: this.normalizeFraction(supplied), detail: 'Прогресс боевого протокола синхронизирован.' };
    }
    if (supplied) {
      const current = supplied.current ?? supplied.completed;
      const total = supplied.total;
      if (typeof supplied.percent === 'number') {
        return { fraction: this.normalizeFraction(supplied.percent), detail: `${Math.round(this.normalizeFraction(supplied.percent) * 100)}% протокола выполнено.` };
      }
      if (typeof current === 'number' && typeof total === 'number' && total > 0) {
        const fraction = this.normalizeFraction(current / total);
        return { fraction, detail: `${Math.round(fraction * 100)}% протокола выполнено.` };
      }
    }

    const waveFraction = Math.min(1, Math.max(0, state.waveKills / waveSize));
    const fraction = ((Math.max(1, state.wave) - 1) + waveFraction) / Math.max(1, state.waveTotal);
    return { fraction: this.normalizeFraction(fraction), detail: 'Разверните протокол и зачистите все волны.' };
  }

  private updateLevelMap(currentLevel: number): void {
    elements<HTMLElement>('.level-node').forEach((node) => {
      const level = Number(node.dataset.level);
      const isCurrent = level === currentLevel;
      const isComplete = level < currentLevel;
      node.classList.toggle('is-current', isCurrent);
      node.classList.toggle('is-complete', isComplete);
      node.classList.toggle('is-locked', level > currentLevel);
      node.setAttribute('aria-current', isCurrent ? 'step' : 'false');
      node.setAttribute('aria-disabled', String(level > currentLevel));
    });
  }

  private updateDrops(drops: Array<UIDrop | string> | undefined, collected = 0): void {
    this.text('#dropsCollectedValue', String(Math.max(0, collected)).padStart(2, '0'));
    const list = optionalElement<HTMLElement>('#dropsList');
    if (!list) return;
    list.replaceChildren();
    if (!drops || drops.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty-drop';
      empty.innerHTML = '<span>◇</span><p>ДРОПЫ НЕ ОБНАРУЖЕНЫ<small>Вернитесь в бой для поиска ресурсов.</small></p>';
      list.append(empty);
      return;
    }

    drops.forEach((drop) => {
      const data = typeof drop === 'string' ? { label: drop } : drop;
      const item = document.createElement('div');
      item.className = `drop-item${data.collected ? ' is-collected' : ''}`;
      if (data.type) item.dataset.dropType = data.type;
      const icon = document.createElement('span');
      icon.className = 'drop-icon';
      icon.textContent = data.icon ?? this.dropIcon(data.type);
      const copy = document.createElement('span');
      copy.className = 'drop-copy';
      const label = document.createElement('strong');
      label.textContent = data.label ?? data.name ?? this.dropLabel(data.type);
      const status = document.createElement('small');
      status.textContent = data.status ?? (data.collected ? 'COLLECTED' : 'AVAILABLE');
      copy.append(label, status);
      item.append(icon, copy);
      if (typeof data.amount === 'number') {
        const amount = document.createElement('b');
        amount.className = 'drop-amount';
        amount.textContent = `+${data.amount}`;
        item.append(amount);
      }
      list.append(item);
    });
  }

  private dropLabel(type: string | undefined): string {
    return type === 'medkit' ? 'РЕМКОМПЛЕКТ' : type === 'shield' ? 'ЩИТОВОЙ МОДУЛЬ' : type === 'baseShield' ? 'ЩИТ БАЗЫ' : type === 'credits' ? 'КРЕДИТНЫЙ КОНТЕЙНЕР' : 'НЕИЗВЕСТНЫЙ РЕСУРС';
  }

  private dropIcon(type: string | undefined): string {
    return type === 'medkit' ? '+' : type === 'shield' ? '◇' : type === 'baseShield' ? '⬡' : type === 'credits' ? '₡' : '•';
  }

  private updateCombo(combo: ComboSnapshot | undefined): void {
    const hud = optionalElement<HTMLElement>('#comboHud');
    if (!hud) return;
    const visible = Boolean(combo && combo.count >= 2 && combo.remaining > 0);
    hud.classList.toggle('is-visible', visible);
    hud.setAttribute('aria-hidden', String(!visible));
    if (!combo) return;
    this.text('#comboCount', String(combo.count).padStart(2, '0'));
    this.text('#comboMultiplier', `×${combo.multiplier}`);
    this.setMeter('#comboMeter', combo.remaining / combo.duration);
  }

  private updateWeapon(state: UIState): void {
    const config = WEAPONS[state.weapon];
    const slot = (['electro', 'flame', 'rocket'] as const).indexOf(state.weapon) + 1;
    this.text('#activeWeaponName', config.name);
    this.text('#weaponHudSlot', String(slot));
    this.text('#weaponHudValue', config.name);
    this.text('#weaponDamage', (config.damage + state.upgrades.power * 0.42).toFixed(1));
    this.text('#weaponRate', `${(1 / (config.cooldown * (1 - state.upgrades.cooling * 0.12))).toFixed(1)}/s`);
    this.text('#weaponRange', config.range);
    elements<HTMLElement>('.weapon-card').forEach((card) => {
      const kind = card.dataset.weapon as WeaponKind;
      const unlocked = state.unlocked[kind];
      card.classList.toggle('locked', !unlocked);
      card.classList.toggle('unlocked', unlocked);
      card.setAttribute('aria-disabled', String(!unlocked));
    });
    this.setWeapon(state.weapon);
  }

  private updateUpgrade(kind: UpgradeKind, state: UIState): void {
    elements<HTMLElement>(`#${kind}Pips i`).forEach((pip, index) => pip.classList.toggle('on', index < state.upgrades[kind]));
    const button = optionalElement<HTMLButtonElement>(`.buy-button[data-upgrade="${kind}"]`);
    if (!button) return;
    const cost = UPGRADE_BASE_COSTS[kind] + state.upgrades[kind] * 55;
    this.text('.upgrade-cost', state.upgrades[kind] >= 3 ? 'MAX' : String(cost), button);
    button.disabled = state.upgrades[kind] >= 3;
  }

  private setHealthTone(valueSelector: string, meterSelector: string, fraction: number): void {
    const critical = fraction < 0.35;
    const value = optionalElement<HTMLElement>(valueSelector);
    const meter = optionalElement<HTMLElement>(meterSelector);
    if (value) value.style.color = critical ? 'var(--red)' : '';
    if (meter) {
      meter.style.background = critical ? 'var(--red)' : '';
      meter.style.boxShadow = critical ? '0 0 13px rgba(239,109,103,.75)' : '';
    }
  }

  private setMeter(selector: string, fraction: number): void {
    const meter = optionalElement<HTMLElement>(selector);
    if (meter) meter.style.width = `${this.normalizeFraction(fraction) * 100}%`;
  }

  private normalizeFraction(value: number): number {
    const fraction = value > 1 ? value / 100 : value;
    return Math.min(1, Math.max(0, Number.isFinite(fraction) ? fraction : 0));
  }

  private onClick(selector: string, handler: () => void): void {
    optionalElement<HTMLElement>(selector)?.addEventListener('click', handler);
  }

  private text(selector: string, value: string, root: ParentNode = document): void {
    root.querySelector<HTMLElement>(selector)?.replaceChildren(document.createTextNode(value));
  }

  private range(selector: string, value: number): void {
    const input = optionalElement<HTMLInputElement>(selector);
    if (input) input.value = String(value);
  }

  private optionalAttribute(selector: string, attribute: string, value: string): void {
    optionalElement<HTMLElement>(selector)?.setAttribute(attribute, value);
  }

  private optionalClass(selector: string, className: string, enabled: boolean): void {
    optionalElement<HTMLElement>(selector)?.classList.toggle(className, enabled);
  }
}
